import React, { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, TextField, IconButton, Grid } from "@mui/material";
import { getAxiosConfig } from "../components/interface/Config";
import { useAuth } from "../context/AuthContext";
import SnackbarMessage from "../components/Snackbar";
import LoadingSpinner from "../components/LoadingSpinner";
import { textFieldStyles } from "../components/Forms/textfield";
import { changePassword } from "../services/passwordService";

const passwordStrengthRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const ChangePasswordForm = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  // Separate states for visibility of each password field
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("info");

  // Validation state
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required.";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required.";
    } else if (!passwordStrengthRegex.test(formData.newPassword)) {
      newErrors.newPassword =
        "New password must be at least 8 characters long, with uppercase, lowercase, number, and special character.";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword =
        "New password and confirmation password do not match.";
    }

    setErrors(newErrors);

    // Return true if there are no errors
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbarSeverity("warning");
      setSnackbarMessage("Please correct the errors in the form.");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      await changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("Password changed successfully!");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error: any) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error.message || "Failed to change password.");
    } finally {
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <div className="mx-auto p-6 bg-white shadow-md rounded-md relative">
      {loading && (
        <LoadingSpinner
          show={false}
          onComplete={function (): void {
            throw new Error("Function not implemented.");
          }}
        />
      )}

      {/* Snackbar for showing messages */}
      <SnackbarMessage
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <div>
            <h2 style={{ fontFamily: "Jost, sans-serif", color: "gray" }}>
              Current Password
            </h2>
            <TextField
              type={showCurrentPassword ? "text" : "password"}
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              sx={textFieldStyles}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={4}>
          <div>
            <h2 style={{ fontFamily: "Jost, sans-serif", color: "gray" }}>
              New Password
            </h2>
            <TextField
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.newPassword}
              sx={textFieldStyles}
              helperText={errors.newPassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={4}>
          <div>
            <h2 style={{ fontFamily: "Jost, sans-serif", color: "gray" }}>
              Confirm Password
            </h2>
            <TextField
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              fullWidth
              sx={textFieldStyles}
              variant="outlined"
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>
        </Grid>
      </Grid>

      <div className="flex justify-center space-x-4 mt-6">
        <Button
          variant="contained"
          sx={{
            backgroundColor: "white",
            border: "2px solid #0870b4",
            color: "#000000",
            transition:
              "background-color 0.3s ease, border-color 0.3s ease, transform 0.3s ease",
            transform: "scale(1)",
            "&:hover": {
              color: "#0870b4",

              borderColor: "#0870b4",
            },
          }}
          onClick={handleCancel}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          className="bg-gradient-to-r from-primary to-primary text-white hover:from-primary hover:to-primary "
          onClick={handleSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
